import React, { useEffect, useMemo, useState } from "react";
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  getAuth,
} from "firebase/auth";
import styled from "styled-components";
import { Body1, Body2, H2 } from "melodies-source/Text";
import { useFormik } from "formik";
import * as yup from "yup";
import { yupRequired } from "Utils/yup";
import { Button } from "melodies-source/Button";
import { FirebaseError } from "firebase/app";
import toast from "react-hot-toast";
import { useCountdown } from "hooks/useCountdown";
import { useUser } from "auth";
import Logo from "svg/Logo";
import { CodeInput } from "Components/CodeInput";

const INITIAL_VALUES = { code: "" };

const VALIDATION_SCHEMA = yup.object().shape({
  code: yupRequired.matches(/^\d{6}$/, "Invalid code"),
});

export const PhoneMFA: React.FC = () => {
  const [recaptchaVerifier, setRecaptchaVerifier] =
    useState<RecaptchaVerifier>(null);
  const [recaptchaVerified, setRecaptchaVerified] = useState(true);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const { resendIn, startCountdown } = useCountdown(60 * 1000);
  const { mfaResolver } = useUser();

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: VALIDATION_SCHEMA,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleVerify,
  });

  const phoneFactorInfo = mfaResolver.hints[0] as PhoneMultiFactorInfo;

  async function handleVerify() {
    try {
      setVerifyCodeLoading(true);
      const cred = PhoneAuthProvider.credential(
        verificationId,
        formik.values.code,
      );
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await mfaResolver.resolveSignIn(multiFactorAssertion);
    } catch (err) {
      const error = err as FirebaseError;
      switch (error.code) {
        case "auth/invalid-verification-code": {
          toast.error("Invalid code");
          break;
        }
        case "auth/code-expired": {
          toast.error("Code has expired");
          break;
        }
        default: {
          console.error(err);
          toast.error("There was an error");
        }
      }
    } finally {
      setVerifyCodeLoading(false);
    }
  }

  const sendCode = async () => {
    try {
      setSendCodeLoading(true);
      const auth = getAuth();

      const phoneInfoOptions = {
        multiFactorHint: mfaResolver.hints[0],
        session: mfaResolver.session,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier,
      );

      startCountdown();
      setVerificationId(verificationId);
    } catch (error) {
      console.error(error);
      toast.error("There was an error");
    } finally {
      setSendCodeLoading(false);
    }
  };

  useEffect(() => {
    const recaptchaVerifier = new RecaptchaVerifier(
      getAuth(),
      "recaptcha-container-id",
      {
        size: "invisible",
        callback: () => setRecaptchaVerified(true),
        "expired-callback": () => setRecaptchaVerified(false),
      },
    );
    recaptchaVerifier.render();
    setRecaptchaVerifier(recaptchaVerifier);
  }, []);

  return (
    <Container>
      <StyledLogo />
      <H2>SMS Authentication</H2>
      {verificationId ? (
        <Body1>
          Please enter the 6-digit verification code sent to your number ending
          in {phoneFactorInfo.phoneNumber.slice(-4)}.
        </Body1>
      ) : (
        <Body1>
          Enter the 6-digit code sent to your number ending in{" "}
          {phoneFactorInfo.phoneNumber.slice(-4)}.
        </Body1>
      )}
      <Recaptcha id="recaptcha-container-id"></Recaptcha>
      {verificationId ? (
        <>
          <Form onSubmit={formik.handleSubmit}>
            <CodeInput
              onChange={(value) => formik.setFieldValue("code", value)}
              size={6}
              error={!!formik.errors.code}
            />
            <StyledButton
              type="submit"
              loading={verifyCodeLoading}
              disabled={verifyCodeLoading || sendCodeLoading}
            >
              Verify
            </StyledButton>
          </Form>
          {resendIn ? (
            <ResendCountdown>Resend in {resendIn}</ResendCountdown>
          ) : (
            <ResendButton onClick={sendCode} loading={sendCodeLoading} text>
              Resend
            </ResendButton>
          )}
        </>
      ) : (
        <StyledButton
          onClick={sendCode}
          loading={sendCodeLoading}
          disabled={!recaptchaVerified}
        >
          Send Code
        </StyledButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${H2} {
    margin-bottom: 15px;
  }

  ${Body1} {
    text-align: center;
    font-weight: 500;
    font-size: 16px;
    color: var(--text-color);
  }

  button {
    width: -webkit-fill-available;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 25px;
`;

const Recaptcha = styled.div`
  margin: 0;
`;

const StyledLogo = styled(Logo)`
  height: 65px;
  margin-bottom: 25px;
`;

const StyledButton = styled(Button)`
  margin-top: 25px;
`;

const ResendCountdown = styled(Body2)`
  margin-top: 15px;
  color: var(--tertiary-text-color);
`;

const ResendButton = styled(Button)`
  margin-top: 5px;
  background-color: transparent !important;
`;
